import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Inject, OnInit, QueryList, ViewChildren } from '@angular/core';
import { ConfigServiceInterface } from '../../core/services/config/config-service.interface';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { AuthServiceInterface, IAgreement } from '../../core/services/auth/auth-service.interface';
import { UserResponse } from '../../core/services/auth/responses/user.response';
import { LocalStorageServiceInterface } from '../../core/services/local-storage/local-storage-service.interface';
import { EventsServiceInterface } from '../../core/services/events/events.interface';
import { DomSanitizer } from '@angular/platform-browser';
import { trackByFn } from '../../core/helpers/trackby.function';
import { getParameterByName } from '../../core/helpers/get-parameter-by-name.function';
import { AnimationItem } from 'lottie-web';
import { ActivatedRoute } from '@angular/router';
import { ResetPasswordService } from './services/reset-password/reset-password.service';
import { errorTextBuilder } from '../../core/helpers/error-text-builder.function';
import { BrowserWindowRef } from '../../core/services/window/window.service';
import { NotificationsServiceInterface } from '../../core/services/notifications/notifications-service.interface';

@Component({
  templateUrl: './new-login-page.component.html',
  providers: [
    {
      provide: 'ResetPasswordServiceInterface',
      useClass: ResetPasswordService,
    },
  ],
})
export class LoginPageComponent implements AfterViewInit, OnInit {
  @ViewChildren('jwtAuthIframeEl') jwtAuthIframeEl: QueryList<ElementRef>;

  public animations = {
    background: {
      options: {
        path: '/assets/animations/sign-in/data.json',
        renderer: 'canvas',
      },
      styles: {
        width: '100%',
        margin: '0 auto',
      },
    },

    reset: {
      options: {
        path: '/assets/animations/reset-password/reset.json',
      },
      styles: {
        maxWidth: '330px',
        maxHeight: '370px',
        margin: '0 auto',
      },
    },

    email: {
      options: {
        path: '/assets/animations/reset-password/email.json',
      },
      styles: {
        maxWidth: '330px',
        maxHeight: '370px',
        margin: '0 auto',
      },
    },

    confirm: {
      options: {
        path: '/assets/animations/reset-password/confirm.json',
      },
      styles: {
        maxWidth: '827px',
        maxHeight: '570px',
        margin: '0 auto',
      },
    },
  };

  public codeLength = 6;
  public is2fa = false;
  public tfaType = 'Telegram';
  public token: string;
  public loadedJwtAuthIframeUrls = 0;
  public jwtAuthIframeUrls = [];
  public user: UserResponse;
  public agreements: IAgreement[] = [];
  public showAgreements = false;
  public isJwtAuthIframeUrlsInited = false;
  public isAuthorized = false;
  public isSubmitted = false;
  public isDoubleError = false;
  public isResetPassword = false;
  public isReset = false;
  public isResetEmail = false;
  public isResetConfirm = false;
  public trackByFn = trackByFn;
  public errorTextBuilder = errorTextBuilder;
  public form = new UntypedFormGroup({
    login: new UntypedFormControl(null, [Validators.required]),
    password: new UntypedFormControl(null, [Validators.required]),
    token_2fa: new UntypedFormControl(null),
  });
  public animationItem: AnimationItem;
  public isVisible = false;

  constructor(
    private sanitizer: DomSanitizer,
    private cd: ChangeDetectorRef,
    @Inject('ConfigServiceInterface') public configService: ConfigServiceInterface,
    @Inject('AuthServiceInterface') private authService: AuthServiceInterface,
    @Inject('LocalStorageServiceInterface') private localStorageService: LocalStorageServiceInterface,
    @Inject('EventsServiceInterface') private eventsService: EventsServiceInterface,
    @Inject(BrowserWindowRef) private window: BrowserWindowRef,
    private activatedRoute: ActivatedRoute,
    @Inject('NotificationsServiceInterface') private notificationsService: NotificationsServiceInterface
  ) {
    this.authService.redirectAfterLoginWithRedirectCheck(this.redirectUrlParam, this.clientIdUrlParam, this.stateUrlParam);
    this.activatedRoute.data.subscribe((data: { userData: UserResponse }) => {
      this.authService.setUser(data.userData);
    });

    this.user = this.authService.getUserData();
    if (this.user) {
      this.isAuthorized = true;
      this.token = this.localStorageService.getItem('token');
    }

    this.agreements = this.authService.getAgreements();
    this.showAgreements = this.agreements.some((agreement) => !agreement.signed_at);
    this.agreements.forEach((agreement) => {
      const isSigned = !!agreement.signed_at;
      this.form.addControl('agreement_' + agreement.id, new UntypedFormControl(isSigned, Validators.requiredTrue));
    });
  }

  ngOnInit(): void {
    this.token2fa.valueChanges.subscribe((value) => {
      if (value && value.length === this.codeLength) {
        this.submit();
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.user) {
      this.initJwtAuthIframeUrls();
    }
  }

  animationCreated(animationItem: AnimationItem): void {
    this.animationItem = animationItem;
  }

  initJwtAuthIframeUrls(): void {
    this.jwtAuthIframeUrls = [];
    this.configService.jwtAuthIframeUrls.forEach((item) => {
      const url = new URL(item);
      this.jwtAuthIframeUrls.push(this.sanitizer.bypassSecurityTrustResourceUrl(url.toString()));
    });
    this.isJwtAuthIframeUrlsInited = true;
    this.cd.detectChanges();
    this.crossDomainAuth();
  }

  crossDomainAuth(): void {
    this.jwtAuthIframeEl.forEach((item) => {
      let childWindow;
      try {
        childWindow = item.nativeElement.contentWindow;
      } catch (e) {
        childWindow = item.nativeElement.contentWindow;
      }
      item.nativeElement.onload = () => {
        childWindow.postMessage(
          JSON.stringify({
            method: 'storage#set',
            key: 'p500:token',
            data: this.token,
          }),
          '*'
        );
        this.loadedJwtAuthIframeUrls++;
        if (this.loadedJwtAuthIframeUrls === this.jwtAuthIframeUrls.length) {
          this.eventsService.broadcast('jwtAuthIframeUrls:loaded');
        }
      };
    });
  }

  resetEmailToggle(): void {
    this.isResetEmail = !this.isResetEmail;
    this.isReset = !this.isReset;
  }

  resetConfirmToggle(): void {
    this.isResetConfirm = !this.isResetConfirm;
    this.isResetEmail = !this.isResetEmail;
  }

  resetPasswordToggle(): void {
    this.isResetPassword = !this.isResetPassword;
    this.isReset = !this.isReset;
  }

  get redirectUrlParam(): string {
    const fullUrl = new URL(this.window.nativeWindow.location.href);
    const redirectUrl = fullUrl.searchParams.get('redirect')
      ? fullUrl.searchParams.get('redirect')
      : fullUrl.searchParams.get('redirect_uri')
      ? fullUrl.searchParams.get('redirect_uri')
      : '';
    return decodeURIComponent(redirectUrl);
  }

  get stateUrlParam(): string {
    const fullUrl = new URL(this.window.nativeWindow.location.href);
    const state = fullUrl.searchParams.get('state') ?? '';
    return state;
  }

  get clientIdUrlParam(): string {
    const fullUrl = new URL(this.window.nativeWindow.location.href);
    const clientId = fullUrl.searchParams.get('client_id') ?? '';
    return clientId;
  }

  get scopeUrlParam(): string {
    const fullUrl = new URL(this.window.nativeWindow.location.href);
    const scope = fullUrl.searchParams.get('scope') ?? '';
    return scope;
  }

  submit(): void {
    if (!this.form.valid) {
      this.isDoubleError = true;
      setTimeout(() => {
        this.isDoubleError = false;
      }, 200);
      return;
    }
    this.isSubmitted = false;

    const data = {
      ...this.form.getRawValue(),
      redirect: this.redirectUrlParam,
      state: this.stateUrlParam,
      client_id: this.clientIdUrlParam,
      scope: this.scopeUrlParam,
    };

    this.authService.auth(
      data,
      () => {
        this.token = this.localStorageService.getItem('token');
        this.user = this.authService.getUserData();
        this.initJwtAuthIframeUrls();

        if (getParameterByName('redirect') || getParameterByName('redirect_uri')) {
          this.authService.redirectAfterLoginWithRedirectCheck(this.redirectUrlParam, this.clientIdUrlParam, this.stateUrlParam);
        } else {
          this.isSubmitted = true;
          this.isAuthorized = true;
        }
      },
      (error) => {
        if (error?.error?.error?.otp) {
          if (this.is2fa) {
            this.notificationsService.add({
              type: 'error',
            });
          }
          this.isDoubleError = false;
          this.token2fa.setValidators(Validators.required);
          this.is2fa = true;
          this.tfaType = this.tfaTypeFromOtp(error.error.error.otp);
        } else {
          this.isDoubleError = false;
          this.isAuthorized = false;
          this.isSubmitted = true;
          this.notificationsService.add({
            type: 'error',
            content: error.error.message,
          });
        }
      }
    );
  }

  tfaTypeFromOtp(otp: string): string {
    switch (otp) {
      case 'sms':
        return 'phone';
      case 'whatsapp':
        return 'WhatsApp';
      case 'telegram':
        return 'Telegram';
      default:
        return otp;
    }
  }

  get login(): UntypedFormControl {
    return this.form.get('login') as UntypedFormControl;
  }

  get password(): UntypedFormControl {
    return this.form.get('password') as UntypedFormControl;
  }

  get token2fa(): UntypedFormControl {
    return this.form.get('token_2fa') as UntypedFormControl;
  }
}
